import { Box, Container, Stack, styled, Typography } from '@mui/material'

export const GolfHoleByHoleContainer = styled(Container)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
  marginTop: theme.spacing(2),
  minWidth: '100%',
  fontSize: '0.875rem',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: '0.75rem'
  }
}))

export const GolfHoleByHoleGridWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 2px 0 rgba(255, 255, 255, 0.1)'
      : '0 0 2px 0 rgba(0, 0, 0, 0.25)',
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
}))

export const GolfHoleByHoleGrid = styled(Box)(() => ({
  display: 'grid'
}))

export const HoleByHoleColumnHeaderItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlight' && prop !== 'disableStretch'
})<{ highlight?: boolean; disableStretch?: boolean }>(({ highlight, disableStretch, theme }) => ({
  minWidth: theme.spacing(7),
  maxWidth: disableStretch ? theme.spacing(12) : 'none',
  height: 'inherit',
  padding: 1.25,
  flexShrink: 0,
  flexGrow: 1,
  flexBasis: 0,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: highlight
    ? theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.grey[800]
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
  color: highlight
    ? theme.palette.mode === 'dark'
      ? theme.palette.grey[50]
      : theme.palette.grey[50]
    : theme.palette.text.primary,
  fontWeight: highlight ? 600 : 400,
  [theme.breakpoints.down('sm')]: {
    minWidth: theme.spacing(4),
    padding: theme.spacing(0.5, 0.25),
    fontSize: '0.7rem'
  }
}))

export const HoleByHoleRowHeaderItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isScoreRow'
})<{ isScoreRow?: boolean }>(({ theme, isScoreRow }) => ({
  width: theme.spacing(8),
  height: 'inherit',
  position: 'sticky',
  left: 0,
  padding: theme.spacing(0.25, 1),
  flexDirection: 'row',
  alignItems: 'center',
  flexShrink: 0,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  color: theme.palette.text.primary,
  fontWeight: isScoreRow ? 600 : 400,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '2px 0 3px -1px rgba(255, 255, 255, 0.1)'
      : '2px 0 3px -1px rgba(0, 0, 0, 0.35)',
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(5),
    padding: theme.spacing(0.25, 0.5),
    fontSize: '0.7rem'
  }
}))

export const HoleByHoleRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isScoreRow' && prop !== 'isCurrentRound'
})<{ isScoreRow?: boolean; isCurrentRound: boolean }>(({ theme, isScoreRow, isCurrentRound }) => ({
  flexDirection: 'row',
  backgroundColor: isScoreRow
    ? theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.grey[100]
    : 'transparent',
  minHeight: theme.spacing(4),
  fontWeight: isScoreRow && isCurrentRound ? 600 : 400,
  [theme.breakpoints.down('sm')]: {
    minHeight: theme.spacing(3)
  }
}))

export const HoleByHoleRowItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'disableStretch'
})<{ disableStretch?: boolean }>(({ theme, disableStretch }) => ({
  minWidth: theme.spacing(7),
  maxWidth: disableStretch ? theme.spacing(12) : 'none',
  height: 'inherit',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  flexGrow: 1,
  flexBasis: 0,
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    minWidth: theme.spacing(4)
  }
}))

export const GolfHoleScore = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'background' && prop !== 'borderRadius' && prop !== 'textColor'
})<{
  background?: string
  borderRadius?: string
  textColor?: string
}>(({ background, borderRadius, textColor, theme }) => ({
  padding: theme.spacing(1, 2),
  height: theme.spacing(4.5),
  margin: 1,
  color: textColor || theme.palette.text.primary,
  backgroundColor: background || 'inherit',
  borderRadius: borderRadius || '50%',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 0.75),
    height: theme.spacing(3),
    margin: theme.spacing(0.25),
    fontSize: '0.7rem'
  }
}))

export const TotalScores = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  position: 'sticky',
  right: 0,
  marginLeft: 1,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
  boxShadow:
    theme.palette.mode === 'dark'
      ? '-2px 0 3px -1px rgba(255, 255, 255, 0.1)'
      : '-2px 0 3px -1px rgba(0, 0, 0, 0.25)'
}))

export const GolfItemColor = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'background'
})<{ background?: string }>(({ background, theme }) => ({
  height: theme.spacing(2.5),
  width: theme.spacing(2.5),
  margin: theme.spacing(0, 1),
  backgroundColor: background,
  borderRadius: '50%',
  [theme.breakpoints.down('sm')]: {
    height: theme.spacing(2),
    width: theme.spacing(2),
    margin: theme.spacing(0, 0.5)
  }
}))

export const GolfKeyAndValue = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  margin: theme.spacing(0, 0.5)
}))

export const GolfItemValue = styled(Typography)(({ theme }) => ({
  margin: 0,
  fontSize: theme.spacing(1.5),
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(1.25)
  }
}))

export const GolfScoreLegendContainer = styled(Stack)(({ theme }) => ({
  height: theme.spacing(3.75),
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: 1,
  width: 'fit-content',
  marginLeft: 'auto',
  paddingRight: 1.25,
  [theme.breakpoints.down('sm')]: {
    height: theme.spacing(3),
    marginBottom: 0.5,
    paddingRight: 0.5
  }
}))
